import type { Component } from 'vue';
import { markRaw } from 'vue';
import {
  BuildingBankIcon,
  CalendarIcon,
  CashBanknoteOffIcon,
  CashIcon,
  CertificateIcon,
  CoinEuroIcon,
  ClockCogIcon,
  ClockIcon,
  HourglassEmptyIcon,
  MailIcon,
  ManIcon,
  ReceiptRefundIcon,
  ReportMoneyIcon,
  SchoolIcon,
  SendIcon,
  UserCogIcon,
  UsersIcon,
  WomanIcon
} from 'vue-tabler-icons';
import type { DisplayConfig } from '@/models/metricDefinitions';

export type MetricCategory = 'dashboard' | 'finance' | 'staff';

export interface MetricDefinition {
  readonly key: string;
  readonly label: string;
  readonly icon?: Component;
  readonly formatAsCurrency?: boolean;
  readonly formatAsDate?: boolean;
  readonly dataPath: string;
  readonly subDataPath?: string; // The same value in another unit, shown small below the value
  readonly subUnit?: string;
  readonly targetPath?: string;
  readonly order: number;
  readonly category: MetricCategory;
  readonly unit?: string;
  readonly aggregationType?: 'sum' | 'average';
  readonly weightPath?: string; // Weights each location's value by this path when averaging
  readonly shareOf?: readonly string[]; // Shows `dataPath` as a percentage of the sum of these paths, aggregated from the sums

  readonly decimalPlaces?: number;
}

export const METRICS: MetricDefinition[] = [
  // Dashboard metrics
  {
    key: 'activeStudents',
    label: 'Anzahl Schüler',
    icon: SchoolIcon,
    dataPath: 'activeStudents',
    targetPath: 'studentTarget',
    order: 1,
    category: 'dashboard',
    aggregationType: 'sum'
  },
  {
    key: 'activeEmployees',
    label: 'Anzahl Mitarbeiter',
    icon: UsersIcon,
    dataPath: 'activeEmployees',
    order: 2,
    category: 'dashboard',
    aggregationType: 'sum'
  },
  {
    key: 'feeSum',
    label: 'Summe Schulgelder',
    icon: CashIcon,
    formatAsCurrency: true,
    dataPath: 'feeSum',
    order: 3,
    category: 'dashboard',
    aggregationType: 'sum'
  },
  // Finance metrics
  {
    key: 'schoolFees',
    label: 'Schulgelder (monatlich)',
    icon: CashIcon,
    formatAsCurrency: true,
    dataPath: 'feeSum',
    order: 1,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'schoolFeesActive',
    label: 'Schulgelder (aktive Schüler)',
    icon: CashIcon,
    formatAsCurrency: true,
    dataPath: 'feeSumActive',
    order: 2,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'schoolFeesDifference',
    label: 'Schulgelder (Abgänger)',
    icon: CashIcon,
    formatAsCurrency: true,
    dataPath: 'feeSumDifference',
    order: 3,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'payingStudents',
    label: 'Zahlende Schüler',
    icon: SchoolIcon,
    dataPath: 'payingStudents',
    order: 4,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'lastBankImport',
    label: 'Letzter Bankimport',
    icon: CalendarIcon,
    formatAsDate: true,
    dataPath: 'lastBankImportAt',
    order: 5,
    category: 'finance'
  },
  {
    key: 'openPaymentReminderSum',
    label: 'Offene Zahlungserinnerungen (gesamt)',
    icon: CashBanknoteOffIcon,
    formatAsCurrency: true,
    dataPath: 'openPaymentReminderSum',
    order: 7,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'paymentRemindersSentLastMonth',
    label: 'Zahlungserinnerungen (letzte 30 Tage)',
    icon: MailIcon,
    dataPath: 'paymentRemindersSentLastMonth',
    order: 8,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'lastPaymentReminderDate',
    label: 'Letzte Zahlungserinnerung',
    icon: CalendarIcon,
    formatAsDate: true,
    dataPath: 'lastPaymentReminderDate',
    order: 6,
    category: 'finance'
  },
  {
    key: 'financialCertsCreated',
    label: 'Finanzbescheinigungen erstellt (ausgewähltes Jahr)',
    icon: CertificateIcon,
    dataPath: 'financialCertsCreated',
    order: 9,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'financialCertsSent',
    label: 'Finanzbescheinigungen versendet (ausgewähltes Jahr)',
    icon: SendIcon,
    dataPath: 'financialCertsSent',
    order: 10,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'schoolFeeReductionCount',
    label: 'Anzahl Schulgeldreduzierungen',
    icon: ReceiptRefundIcon,
    dataPath: 'schoolFeeReductionCount',
    order: 11,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'schoolFeeReductionTotal',
    label: 'Summe Schulgeldreduzierungen (monatlich)',
    icon: ReceiptRefundIcon,
    formatAsCurrency: true,
    dataPath: 'schoolFeeReductionTotal',
    order: 12,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'bankBalance',
    label: 'Kontostand',
    icon: BuildingBankIcon,
    formatAsCurrency: true,
    dataPath: 'bankBalance',
    order: 14,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'totalDifference',
    label: 'Gesamtforderung (dieses Schuljahr)',
    icon: ReportMoneyIcon,
    formatAsCurrency: true,
    dataPath: 'totalDifference',
    order: 13,
    category: 'finance',
    aggregationType: 'sum'
  },
  {
    key: 'averageSchoolFee',
    label: 'Durchschnittliches Schulgeld',
    icon: CoinEuroIcon,
    formatAsCurrency: true,
    dataPath: 'averageSchoolFee',
    order: 15,
    category: 'finance',
    aggregationType: 'average',
    weightPath: 'payingStudents'
  },
  // Staff metrics
  {
    key: 'averageAge',
    label: 'Durchschnittsalter',
    icon: HourglassEmptyIcon,
    dataPath: 'averageAge',
    order: 1,
    category: 'staff',
    unit: 'Jahre',
    aggregationType: 'average',
    decimalPlaces: 1
  },
  {
    key: 'maleStaff',
    label: 'männliche Mitarbeiter',
    icon: ManIcon,
    dataPath: 'amountMale',
    order: 2,
    category: 'staff',
    aggregationType: 'sum'
  },
  {
    key: 'femaleStaff',
    label: 'weibliche Mitarbeiterinnen',
    icon: WomanIcon,
    dataPath: 'amountFemale',
    order: 3,
    category: 'staff',
    aggregationType: 'sum'
  },
  {
    key: 'teacherCount',
    label: 'Anzahl PMA',
    icon: UsersIcon,
    dataPath: 'teacherCount',
    order: 4,
    category: 'staff',
    aggregationType: 'sum'
  },
  {
    key: 'pedagogicalHours',
    label: 'PMA-Stunden',
    icon: ClockIcon,
    dataPath: 'pedagogicalLessons',
    subDataPath: 'pedagogicalHours',
    subUnit: 'Std.',
    order: 5,
    category: 'staff',
    unit: 'UStd.',
    aggregationType: 'sum'
  },
  {
    key: 'pedagogicalHoursShare',
    label: 'Anteil PMA-Stunden',
    icon: ClockIcon,
    dataPath: 'pedagogicalHours',
    shareOf: ['pedagogicalHours', 'nonPedagogicalHours'],
    order: 6,
    category: 'staff',
    unit: '%',
    decimalPlaces: 1
  },
  {
    key: 'staffCount',
    label: 'Anzahl VMA',
    icon: UserCogIcon,
    dataPath: 'staffCount',
    order: 7,
    category: 'staff',
    aggregationType: 'sum'
  },
  {
    key: 'nonPedagogicalHours',
    label: 'VMA-Stunden',
    icon: ClockCogIcon,
    dataPath: 'nonPedagogicalHours',
    order: 8,
    category: 'staff',
    unit: 'Std.',
    aggregationType: 'sum'
  },
  {
    key: 'nonPedagogicalHoursShare',
    label: 'Anteil VMA-Stunden',
    icon: ClockCogIcon,
    dataPath: 'nonPedagogicalHours',
    shareOf: ['pedagogicalHours', 'nonPedagogicalHours'],
    order: 9,
    category: 'staff',
    unit: '%',
    decimalPlaces: 1
  }
];

export const getDisplayConfig = (metric: MetricDefinition): DisplayConfig => ({
  label: metric.label,
  icon: metric.icon ? markRaw(metric.icon) : undefined,
  formatAsCurrency: metric.formatAsCurrency,
  formatAsDate: metric.formatAsDate,
  unit: metric.unit,
  subUnit: metric.subUnit,
  decimalPlaces: metric.decimalPlaces
});

export const getMetricsByCategory = (category: MetricCategory): MetricDefinition[] => {
  return METRICS.filter((metric) => metric.category === category).sort((a, b) => a.order - b.order);
};
